import { ThemeEvents, VariantUpdateEvent } from '@theme/events';

/**
 * A custom element that displays a product message.
 * This component listens for variant update events and updates the message display accordingly.
 * It handles message updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 */
class ProductMessage extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateMessage);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateMessage);
  }

  /**
   * Updates the message.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateMessage = (event) => {
    const newMessage = event.detail.data.html.querySelector('product-message[ref="messageSoldout"]');
    const currentMessage = this;

    if (!newMessage || !currentMessage) return;

    if (currentMessage.innerHTML !== newMessage.innerHTML) {
      currentMessage.replaceWith(newMessage);
    }
  };
}

if (!customElements.get('product-message')) {
  customElements.define('product-message', ProductMessage);
}
class ProductSKU extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateSKU);
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateSKU);
  }

  /**
   * Updates the sku.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSKU = (event) => {
    const newSKU = event.detail.data.html.querySelector('product-sku[ref="productSku"]');
    const currentSKU = this;

    if (!newSKU || !currentSKU) return;

    if (currentSKU.innerHTML !== newSKU.innerHTML) {
      currentSKU.replaceWith(newSKU);
    }
  };
}

if (!customElements.get('product-sku')) {
  customElements.define('product-sku', ProductSKU);
}

class ProductStickyBar extends HTMLElement {
  connectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updateSticky);
    this.initStickyAddSelect();
    this.initSticky();
  }

  disconnectedCallback() {
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updateSticky);
  }

  initSticky() {
    const stickyEl = document.querySelector('.product-sticky-add');
    const addToCartBtn = document.querySelector('[name="add"]');

    if (!stickyEl || !addToCartBtn) return;

    let isSticky = false;

    const getButtonBottom = () => {
      const rect = addToCartBtn.getBoundingClientRect();
      return rect.bottom + window.scrollY;
    };

    let buttonBottom = getButtonBottom();

    window.addEventListener('resize', () => {
      buttonBottom = getButtonBottom();
    });

    window.addEventListener(
      'scroll',
      () => {
        const scrollTop = window.scrollY;

        if (scrollTop > buttonBottom && !isSticky) {
          stickyEl.classList.add('is-sticky');
          isSticky = true;
        }

        if (scrollTop <= buttonBottom && isSticky) {
          stickyEl.classList.remove('is-sticky');
          isSticky = false;
        }
      },
      { passive: true }
    );
  }

  initStickyAddSelect() {
    this.select = this.querySelector('.variant-option--dropdowns:not(.hidden) select');
    this.actionWrapper = this.querySelector('.product-sticky-add__wapper');

    if (!this.select || !this.actionWrapper) return;

    const updateState = () => {
      const value = this.select?.value;
      if (value) {
        this.actionWrapper?.classList.add('allow_add');
        const variantPicker = document.querySelector(".product-details .variant-picker");
        if (variantPicker) {
          const variantSize = variantPicker.querySelector(`input[value="${value}"]`);
          if (variantSize) {
            variantSize.click();
          }
        }
      } else {
        this.actionWrapper?.classList.remove('allow_add');
      }
    };
    this.select.addEventListener('change', updateState);
  }

  /**
   * Updates the sticky.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateSticky = (event) => {
    const newSticky = event.detail.data.html.querySelector('sticky-addtocart .product-sticky-add__flex');
    const currentSticky = this.querySelector(".product-sticky-add__flex");

    if (!newSticky || !currentSticky) return;

    if (currentSticky.innerHTML !== newSticky.innerHTML) {
      currentSticky.replaceWith(newSticky);
      this.initStickyAddSelect();
    }
  };
}

if (!customElements.get('sticky-addtocart')) {
  customElements.define('sticky-addtocart', ProductStickyBar);
}

document.addEventListener("DOMContentLoaded", function () {
  document.querySelectorAll('.product-media-container__zoom-hover').forEach(container => {
    const img = container.querySelector('img');
    const mediaGallery = container.closest('media-gallery');
    const zoomScale = mediaGallery ? mediaGallery.getAttribute('data-zoom-scale') : '2';
    
    container.addEventListener('mousemove', e => {
      const rect = container.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      img.style.setProperty('--x', `${x}%`);
      img.style.setProperty('--y', `${y}%`);
      img.style.transform = `scale(${zoomScale})`;
    });

    container.addEventListener('mouseleave', () => {
      img.style.transform = 'scale(1)';
      img.style.setProperty('--x', '50%');
      img.style.setProperty('--y', '50%');
    });
  });
});
const TARGET_SELECTOR = '.jdgm-write-review-modal__negative-resolution-title';

function convertImmediately(h2) {
  if (h2.dataset.converted) return;

  const span = document.createElement('span');
  span.className = h2.className;
  span.textContent = h2.textContent;

  h2.replaceWith(span);
}

const observer = new MutationObserver((mutations) => {
  for (const m of mutations) {
    for (const node of m.addedNodes) {
      if (!(node instanceof HTMLElement)) continue;

      const h2 = node.matches?.(TARGET_SELECTOR)
        ? node
        : node.querySelector?.(TARGET_SELECTOR);

      if (h2) {
        convertImmediately(h2);
        observer.disconnect();
        return;
      }
    }
  }
});

observer.observe(document.body, { childList: true, subtree: true });